import { gql } from '@apollo/client';

// ========== QUERIES ==========

// this is an expensive query. it fetches all the mesh runs for the given
// organization that were "startedAt" within the given date range.
// It involves splitting graph into meshes
// and then fetching mesh runs (which includes instances) for each mesh.
// This query is used to render the list view of Data Pipeline.

export const GET_RECENT_MESH_RUNS = gql`
  query GetRecentMeshRuns(
    $organizationId: UUID!
    $creditFacilitySlugsToHide: [String!]
    $startDate: JSDate
    $endDate: JSDate
  ) {
    meshRuns: getRecentMeshRuns(
      organizationId: $organizationId
      creditFacilitySlugsToHide: $creditFacilitySlugsToHide
      startDate: $startDate
      endDate: $endDate
    )
  }
`;

export const GET_LATEST_MESH_RUN_FOR_IMPLEMENTATION = gql`
  query GetLatestMeshRun($implementationId: UUID!) {
    getLatestMeshRun(implementationId: $implementationId)
  }
`;

// Fetches all mesh runs across all implementations (superuser only).
// Used to render the global "Recent Pipeline Runs" admin view.
export const GET_ALL_RECENT_MESH_RUNS = gql`
  query GetAllRecentMeshRuns(
    $startDate: JSDate
    $endDate: JSDate
    $limit: Int
  ) {
    allMeshRuns: getAllRecentMeshRuns(
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    )
  }
`;

// the RDFV of one implementation's pipeline runs within the given date range.
// The upload drawer's calendar guesses the next version of the selected mesh from
// it, so it must count that implementation's runs only: the server starts the next
// version of that implementation.
export const GET_IMPLEMENTATION_PIPELINE_RUNS = gql`
  query GetImplementationPipelineRuns(
    $implementationId: UUID!
    $startDate: FullDateStr
    $endDate: FullDateStr
  ) {
    getPipelineRuns(
      implementationId: $implementationId
      startDate: $startDate
      endDate: $endDate
    ) {
      reportingDate
      fileVersion
    }
  }
`;

// this is much lighter weight query. it fetches just RDFV data for pipeline runs
// of every implementation of the given borrower organization within the given
// date range.
export const GET_BORROWER_PIPELINE_RUNS = gql`
  query GetBorrowerPipelineRuns(
    $borrowerOrganizationId: UUID!
    $startDate: FullDateStr
    $endDate: FullDateStr
  ) {
    getPipelineRuns(
      organizationId: $borrowerOrganizationId
      startDate: $startDate
      endDate: $endDate
    ) {
      reportingDate
      fileVersion
    }
  }
`;

// Returns pipeline runs with the list of artifact IDs that have instances uploaded.
// Used by deliverables to filter out versions where the artifact already exists.
export const GET_PIPELINE_RUNS_WITH_ARTIFACT_IDS = gql`
  query GetPipelineRunsWithArtifactIds(
    $borrowerOrganizationId: UUID!
    $startDate: FullDateStr
    $endDate: FullDateStr
  ) {
    getPipelineRunsWithArtifactIds(
      organizationId: $borrowerOrganizationId
      startDate: $startDate
      endDate: $endDate
    ) {
      reportingDate
      fileVersion
      uploadedArtifactIds
    }
  }
`;

export const GET_MAYBE_COMPLETE_MESH_RUN_RECORD = gql`
  query GetMaybeCompleteMeshRunRecord(
    $meshRunId: String
    $meshRunParams: MeshRunParams
  ) {
    maybeCompleteMeshRunRecord: getCompleteMeshRunRecordIfAvailable(
      meshRunId: $meshRunId
      meshRunParams: $meshRunParams
    )
  }
`;

export const GET_MESH_DOCUMENT = gql`
  query GetMeshDocument($meshDocumentId: String!) {
    meshDocument: getMeshDocument(meshDocumentId: $meshDocumentId)
  }
`;

export const GET_LATEST_MESH_DOCUMENT_METADATA = gql`
  query GetLatestMeshDocumentMetadata(
    $organizationId: UUID!
    $creditFacilitySlugsToHide: [String!]
  ) {
    latestMeshDocumentMetadata: getLatestMeshDocumentMetadata(
      organizationId: $organizationId
      creditFacilitySlugsToHide: $creditFacilitySlugsToHide
    )
  }
`;

export const GET_DATA_PIPELINE_DOWNLOAD_SIGNED_URLS = gql`
  query getDataPipelineDownloadSignedUrl($serializedParams: String!) {
    downloadUrls: getSignedDownloadUrls(
      feature: "dataPipelineInstanceRecord"
      serializedParams: $serializedParams
    ) {
      url
      filename
    }
  }
`;

export const GET_DATA_PIPELINE_UPLOAD_SIGNED_URL = gql`
  query getDataPipelineUploadSignedUrl($serializedParams: String!) {
    getSignedUploadUrl(
      feature: "getDataPipelineUploadDestination"
      serializedParams: $serializedParams
    ) {
      url
      s3RequestFields
      queryResponseFields
    }
  }
`;

// The runs of a reportingDate, newest first, with what each received for the given
// inputs (e.g. one mesh's). Implementation-wide, unlike a mesh run's record.
export const GET_PIPELINE_RUN_INPUTS = gql`
  query GetPipelineRunInputs(
    $implementationId: UUID!
    $reportingDate: FullDateStr!
    $artifactSlugs: [String!]!
  ) {
    getPipelineRunInputs(
      implementationId: $implementationId
      reportingDate: $reportingDate
      artifactSlugs: $artifactSlugs
    ) {
      pipelineRunId
      reportingDate
      fileVersion
      createdAt
      isLatest
      isStale
      inputs {
        artifactSlug
        status
        uploadedAt
      }
    }
  }
`;

// Opens a submission of a reportingDate's inputs: starts the next version or fills
// in an existing one.
export const CREATE_PIPELINE_SUBMISSION = gql`
  mutation CreatePipelineSubmission(
    $implementationId: UUID!
    $reportingDate: FullDateStr!
    $fileVersion: Int!
    $newRun: Boolean!
    $inputs: [PipelineSubmissionInput!]!
  ) {
    createPipelineSubmission(
      implementationId: $implementationId
      reportingDate: $reportingDate
      fileVersion: $fileVersion
      newRun: $newRun
      inputs: $inputs
    ) {
      fileVersion
      uploads {
        artifactSlug
        url
        fields {
          name
          value
        }
      }
    }
  }
`;
